import { useState } from "react";
import { api } from "../api";
import { DrawCanvas } from "../components/DrawCanvas";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface View {
  role: "artist" | "guesser";
  artist: string;
  len: number;
  hasDrawing: boolean;
  png?: string;
  word?: string;
  attempts: string[];
  letter?: string;
}

const COPY = {
  artist_draws: "Ти сьогодні малюєш, не вгадуєш 🎨",
  no_drawing_yet: "Художник ще малює",
  bad_word: "Одне українське слово",
  already_guessed: "Вже було",
  not_artist: "Сьогодні малює хтось інший",
  bad_drawing: "Щось не так з малюнком, спробуй ще раз",
};

export function LegacyMaliunok({ date }: { date?: string | undefined }) {
  const g = useGame<View>("maliunok", COPY, date);
  const [word, setWord] = useState("");

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  const submit = async () => {
    const w = word.trim();
    if (!w || finished || g.busy) return;
    const next = await g.run(() => api.guess<View>("maliunok", w, date));
    if (next) {
      const extra = next.extra as { right?: boolean } | undefined;
      if (extra?.right === false) {
        haptic.error();
        g.showToast("Ні, не воно");
      }
      setWord("");
    }
  };

  const saveDrawing = async (png: string) => {
    await g.run(() => api.drawing<View>(png, date));
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const isArtist = v.role === "artist";

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="МАЛЮНОК" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        {isArtist ? (
          <>
            <div className="mt-1 rounded-2xl bg-tgsection px-4 py-3 text-center">
              <div className="text-[11px] uppercase tracking-wide text-tghint">намалюй це слово</div>
              <div className="mt-1 text-2xl font-extrabold tracking-[0.12em]">{(v.word ?? "").toUpperCase()}</div>
              <div className="mt-1 text-xs text-tghint">без літер і цифр на малюнку - тільки картинка 🙃</div>
            </div>
            <div className="mt-3">
              {v.hasDrawing && finished ? (
                <div className="text-center">
                  {v.png && <img src={v.png} alt="" className="mx-auto w-full max-w-[384px] rounded-xl bg-white shadow-lg" />}
                  <div className="mt-2 text-sm text-tghint">малюнок у чаті - дивись, як вгадують 🎨</div>
                </div>
              ) : (
                <DrawCanvas disabled={false} busy={g.busy} onSave={(png) => void saveDrawing(png)} />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="mt-1 text-center text-xs text-tghint">
              малює <b className="text-tgtext">{v.artist}</b> · слово на {v.len} літер
              {v.letter ? ` · починається на «${v.letter}»` : ""}
            </div>
            {v.hasDrawing && v.png ? (
              <img src={v.png} alt="" className="mx-auto mt-2 w-full max-w-[384px] rounded-xl bg-white shadow-lg" />
            ) : (
              <div className="mx-auto mt-2 flex aspect-square w-full max-w-[384px] items-center justify-center rounded-xl bg-tgsection text-sm text-tghint">
                художник ще малює 🎨
              </div>
            )}
            {v.attempts.length > 0 && (
              <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                {v.attempts.map((a) => (
                  <span key={a} className={`rounded-full px-2.5 py-1 text-xs ${finished && won && a === v.attempts.at(-1) ? "bg-ok/20 font-bold" : "bg-tgsection text-tghint"}`}>
                    {a}
                  </span>
                ))}
              </div>
            )}
            {finished && v.word && !won && (
              <div className="mt-2 text-center text-sm">
                це був <b>{v.word.toUpperCase()}</b>
              </div>
            )}
          </>
        )}
      </div>

      {!isArtist && (
        <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
          <form
            className="flex gap-2 px-4 pb-1"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <input
              value={word}
              onChange={(e) => setWord(e.target.value)}
              readOnly={finished || !v.hasDrawing}
              placeholder={finished ? "гра закінчена" : v.hasDrawing ? "що це?" : "чекаємо на малюнок…"}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              enterKeyHint="send"
              className="h-[44px] flex-1 rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 text-[16px] text-tgtext outline-none placeholder:text-[rgba(128,128,128,0.7)] focus:border-tgbutton disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={finished || g.busy || !word.trim() || !v.hasDrawing}
              onMouseDown={(e) => e.preventDefault()}
              className="h-[44px] rounded-xl bg-tgbutton px-5 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
            >
              {g.busy ? "…" : "→"}
            </button>
          </form>
          {!finished && v.attempts.length >= 3 && (
            <div className="flex px-4">
              <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("maliunok", date))} />
            </div>
          )}
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={isArtist ? "Шедевр здано!" : won ? "Вгадав!" : "Не сьогодні"}
          subtitle={
            isArtist
              ? "тепер дивись, як чат страждає 🎨"
              : won
                ? `за ${v.attempts.length} · стрік ${s.me.streak} 🔥`
                : `це був ${v.word?.toUpperCase() ?? "?"} · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n ?? "–"}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
